(function () {
  const SHOW_DELAY_MS = 0;
  const FINISH_MS = 220;
  const MIN_VISIBLE_MS = 180;
  const FAILSAFE_MS = 5000;
  const TAP_START_DELAY_MS = 90;
  const TAP_SCROLL_CANCEL_PX = 10;

  let showTimeoutId = null;
  let hideTimeoutId = null;
  let failSafeTimeoutId = null;
  let isVisible = false;
  let paintedAt = 0;
  let finishQueuedUntilPaint = false;
  let tapPointerId = null;
  let tapStartX = 0;
  let tapStartY = 0;
  let tapCanceled = false;
  let tapTimeoutId = null;

  /** @type {HTMLElement | null} */
  let progressRoot = null;
  /** @type {HTMLElement | null} */
  let progressBar = null;

  function applyInlinePresentationStyles() {
    if (!(progressRoot instanceof HTMLElement) || !(progressBar instanceof HTMLElement)) return;

    progressRoot.style.position = 'fixed';
    progressRoot.style.left = '0';
    progressRoot.style.right = '0';
    progressRoot.style.top = '0';
    progressRoot.style.width = '100%';
    progressRoot.style.height = '2px';
    progressRoot.style.pointerEvents = 'none';
    progressRoot.style.zIndex = '2147483647';
    progressRoot.style.transition = 'opacity 0.16s ease, transform 0.16s ease';
    progressRoot.style.background = 'rgba(0, 0, 0, 0.02)';
    progressRoot.style.boxShadow = '0 1px 0 rgba(255, 255, 255, 0.08)';
    progressRoot.style.willChange = 'transform, opacity';

    progressBar.style.display = 'block';
    progressBar.style.width = '100%';
    progressBar.style.height = '100%';
    progressBar.style.background = 'rgba(12, 12, 12, 0.9)';
    progressBar.style.transformOrigin = 'left center';
    progressBar.style.transition = 'transform 0.36s cubic-bezier(0.22, 1, 0.36, 1)';
    progressBar.style.willChange = 'transform';
  }

  function setHiddenPresentationState() {
    if (!(progressRoot instanceof HTMLElement)) return;
    progressRoot.style.display = 'block';
    progressRoot.style.visibility = 'hidden';
    progressRoot.style.opacity = '0';
    progressRoot.style.transform = 'translateY(-2px)';
  }

  function setVisiblePresentationState() {
    if (!(progressRoot instanceof HTMLElement)) return;
    progressRoot.style.display = 'block';
    progressRoot.style.visibility = 'visible';
    progressRoot.style.opacity = '0.78';
    progressRoot.style.transform = 'translateY(0)';
  }

  function animateToInitialProgress() {
    if (!(progressBar instanceof HTMLElement)) return;

    // Force a measurable "0 -> 75%" fill on every navigation.
    progressBar.style.transition = 'none';
    progressBar.style.transform = 'scaleX(0)';
    progressBar.getBoundingClientRect();

    progressBar.style.transition = 'transform 0.52s cubic-bezier(0.22, 1, 0.36, 1)';
    requestAnimationFrame(() => {
      progressBar.style.transform = 'scaleX(0.75)';
    });
  }

  function ensureProgressBar() {
    if (progressRoot instanceof HTMLElement && progressBar instanceof HTMLElement) return true;
    if (!(document.body instanceof HTMLBodyElement)) return false;

    const existing = document.getElementById('global-nav-progress');
    if (existing instanceof HTMLElement) {
      progressRoot = existing;
      progressBar = existing.querySelector('.global-nav-progress__bar');
      if (!(progressBar instanceof HTMLElement)) {
        progressBar = document.createElement('span');
        progressBar.className = 'global-nav-progress__bar';
        progressRoot.append(progressBar);
      }
      applyInlinePresentationStyles();
      setHiddenPresentationState();
      return true;
    }

    progressRoot = document.createElement('div');
    progressRoot.id = 'global-nav-progress';
    progressRoot.setAttribute('aria-hidden', 'true');

    progressBar = document.createElement('span');
    progressBar.className = 'global-nav-progress__bar';
    progressRoot.append(progressBar);

    document.body.prepend(progressRoot);
    applyInlinePresentationStyles();
    setHiddenPresentationState();
    return true;
  }

  function clearTimers() {
    if (showTimeoutId != null) {
      clearTimeout(showTimeoutId);
      showTimeoutId = null;
    }

    if (hideTimeoutId != null) {
      clearTimeout(hideTimeoutId);
      hideTimeoutId = null;
    }

    if (failSafeTimeoutId != null) {
      clearTimeout(failSafeTimeoutId);
      failSafeTimeoutId = null;
    }
  }

  function clearTapTracking() {
    if (tapTimeoutId != null) {
      clearTimeout(tapTimeoutId);
      tapTimeoutId = null;
    }
    tapPointerId = null;
    tapStartX = 0;
    tapStartY = 0;
    tapCanceled = false;
  }

  function schedulePaintCommit() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        paintedAt = performance.now();
        if (finishQueuedUntilPaint) {
          finishQueuedUntilPaint = false;
          finishProgress();
        }
      });
    });
  }

  function showProgressFrame() {
    if (!ensureProgressBar()) return false;

    isVisible = true;
    paintedAt = 0;
    progressRoot.classList.add('is-active');
    progressRoot.classList.remove('is-finishing');
    setVisiblePresentationState();
    animateToInitialProgress();
    schedulePaintCommit();

    return true;
  }

  function resetProgress() {
    clearTimers();
    clearTapTracking();

    if (ensureProgressBar()) {
      progressRoot.classList.remove('is-active', 'is-finishing');
      setHiddenPresentationState();
      progressBar.style.transform = 'scaleX(0)';
    }

    isVisible = false;
    paintedAt = 0;
    finishQueuedUntilPaint = false;
  }

  function startProgress() {
    if (isVisible || showTimeoutId != null) return;

    if (hideTimeoutId != null) {
      clearTimeout(hideTimeoutId);
      hideTimeoutId = null;
    }

    showTimeoutId = setTimeout(() => {
      showTimeoutId = null;
      if (!showProgressFrame()) return;

      failSafeTimeoutId = setTimeout(() => {
        resetProgress();
      }, FAILSAFE_MS);
    }, SHOW_DELAY_MS);
  }

  function finishProgress() {
    if (showTimeoutId != null) {
      clearTimeout(showTimeoutId);
      showTimeoutId = null;
    }

    if (!isVisible) return;

    if (!ensureProgressBar()) return;
    if (paintedAt === 0) {
      finishQueuedUntilPaint = true;
      return;
    }

    progressRoot.classList.add('is-active', 'is-finishing');
    setVisiblePresentationState();
    progressBar.style.transition = 'transform 0.18s cubic-bezier(0.22, 1, 0.36, 1)';
    progressBar.style.transform = 'scaleX(1)';

    if (hideTimeoutId != null) {
      clearTimeout(hideTimeoutId);
    }

    const elapsedVisibleMs = Math.max(0, performance.now() - paintedAt);
    const minimumRemainingMs = Math.max(0, MIN_VISIBLE_MS - elapsedVisibleMs);

    hideTimeoutId = setTimeout(() => {
      resetProgress();
    }, Math.max(FINISH_MS, minimumRemainingMs));
  }

  /**
   * @param {MouseEvent} event
   * @returns {boolean}
   */
  function shouldTrackClickNavigation(event) {
    if (event.defaultPrevented) return false;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (!(event.target instanceof Element)) return false;

    const anchor = event.target.closest('a[href]');
    if (!(anchor instanceof HTMLAnchorElement)) return false;

    if (anchor.target && anchor.target !== '_self') return false;
    if (anchor.hasAttribute('download')) return false;

    const href = anchor.getAttribute('href') || '';
    if (href.startsWith('#')) return false;

    const destination = new URL(anchor.href, window.location.href);
    if (destination.origin !== window.location.origin) return false;

    const current = new URL(window.location.href);
    const isSamePage =
      destination.pathname === current.pathname && destination.search === current.search && destination.hash === current.hash;
    if (isSamePage) return false;

    return true;
  }

  /**
   * @param {PointerEvent} event
   * @returns {boolean}
   */
  function shouldTrackPointerNavigation(event) {
    if (event.defaultPrevented) return false;
    if (!(event.target instanceof Element)) return false;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;

    const anchor = event.target.closest('a[href]');
    if (!(anchor instanceof HTMLAnchorElement)) return false;

    if (anchor.target && anchor.target !== '_self') return false;
    if (anchor.hasAttribute('download')) return false;

    const href = anchor.getAttribute('href') || '';
    if (href.startsWith('#')) return false;

    const destination = new URL(anchor.href, window.location.href);
    if (destination.origin !== window.location.origin) return false;

    const current = new URL(window.location.href);
    const isSamePage =
      destination.pathname === current.pathname && destination.search === current.search && destination.hash === current.hash;
    if (isSamePage) return false;

    return true;
  }

  document.addEventListener(
    'pointerdown',
    (event) => {
      if (!(event instanceof PointerEvent)) return;
      if (!shouldTrackPointerNavigation(event)) return;

      if (event.pointerType === 'mouse') {
        startProgress();
        return;
      }

      // Touch/pen: start early, but cancel if gesture becomes a scroll.
      clearTapTracking();
      tapPointerId = event.pointerId;
      tapStartX = event.clientX;
      tapStartY = event.clientY;
      tapCanceled = false;
      tapTimeoutId = setTimeout(() => {
        tapTimeoutId = null;
        if (!tapCanceled) {
          startProgress();
        }
      }, TAP_START_DELAY_MS);
    },
    true
  );

  document.addEventListener(
    'pointermove',
    (event) => {
      if (!(event instanceof PointerEvent)) return;
      if (tapPointerId == null || event.pointerId !== tapPointerId) return;

      const deltaX = Math.abs(event.clientX - tapStartX);
      const deltaY = Math.abs(event.clientY - tapStartY);
      if (deltaX < TAP_SCROLL_CANCEL_PX && deltaY < TAP_SCROLL_CANCEL_PX) return;

      tapCanceled = true;
      if (tapTimeoutId != null) {
        clearTimeout(tapTimeoutId);
        tapTimeoutId = null;
      }
    },
    true
  );

  document.addEventListener(
    'pointerup',
    (event) => {
      if (!(event instanceof PointerEvent)) return;
      if (tapPointerId == null || event.pointerId !== tapPointerId) return;

      if (!tapCanceled && tapTimeoutId != null) {
        clearTimeout(tapTimeoutId);
        tapTimeoutId = null;
        startProgress();
      }

      clearTapTracking();
    },
    true
  );

  document.addEventListener(
    'pointercancel',
    (event) => {
      if (!(event instanceof PointerEvent)) return;
      if (tapPointerId == null || event.pointerId !== tapPointerId) return;
      clearTapTracking();
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      if (event instanceof MouseEvent && shouldTrackClickNavigation(event)) {
        startProgress();
      }
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      if (!(event.target instanceof HTMLFormElement)) return;
      if (event.defaultPrevented) return;
      if (event.target && event.target.target && event.target.target !== '_self') return;
      if (event.target.closest('results-list[infinite-scroll="true"]')) return;
      startProgress();
    },
    true
  );

  window.addEventListener('pageshow', () => {
    resetProgress();
  });

  window.__startGlobalNavProgress = startProgress;
  window.__finishGlobalNavProgress = finishProgress;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', resetProgress, { once: true });
  } else {
    resetProgress();
  }
})();
